import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/contexts/TenantContext';
import { setActiveOrgIdGetter } from '@/lib/api-fetch';
import { useAvailableOrgIds, useHomeOrgId } from '@/lib/api/memberships';
import { usePermissions } from '@/permissions/use-permissions';

/**
 * `useActiveOrgIdSync` (W102-F1-S2) — keep the in-memory active-org id
 * in sync with the user's actual memberships, and register the getter
 * that `apiFetch` uses to stamp the `X-Org-Id` header on outbound
 * requests.
 *
 * Two effects:
 *
 *   1. **Validation + bootstrap.** When the memberships query resolves,
 *      check that the persisted `activePartnerId` (from
 *      `localStorage` / `TenantContext`) is still in the user's
 *      allowed-org set. If not, reset to `homeOrgId` — handles the
 *      "I had access to Partner X yesterday, was removed today,
 *      open the SPA → it would have sent a 403-tripping X-Org-Id
 *      until I manually re-picked an org" footgun. Also bootstraps
 *      the first selection to `homeOrgId` when nothing was previously
 *      persisted.
 *
 *   2. **Getter registration.** `apiFetch` reads `activeOrgIdGetter()`
 *      on every outbound request to decide whether to stamp
 *      `X-Org-Id`. We register a closure over the current
 *      `activePartnerId` so the header always reflects the latest
 *      switcher selection (no stale closure inside `apiFetch`).
 *
 * Mounted exactly once from `AppShell` inside `<TenantProvider>`. Does
 * NOT render anything — pure side-effect hook. Use the
 * `<ActiveOrgIdSync />` component below for ergonomic placement in JSX.
 */
export function useActiveOrgIdSync(): void {
  const { activePartnerId, setActivePartner } = useTenant();
  const availableOrgIds = useAvailableOrgIds();
  const homeOrgId = useHomeOrgId();
  // #1686 — platform admins can read ANY org at Core, so the
  // membership-based "stale value" reset below must not fire for them:
  // their active org is legitimately not a direct membership row (e.g.
  // SustEvo staff acting on a prod org). Resetting them to `homeOrgId`
  // (often a retired demo org) is exactly what wedged the dashboard.
  // Core remains the authority — an org they genuinely cannot read still
  // 403s — so skipping the client-side reset only widens, never escalates.
  // `role` is the resolved tier — the JWT `platform_admin` App Role
  // normalizes to the `'platform'` tier (SustEvo super-admins).
  const { role } = usePermissions();
  const isPlatformAdmin = role === 'platform';

  // Effect 1 — validate + bootstrap.
  useEffect(() => {
    if (availableOrgIds.length === 0) {
      // Memberships not loaded yet (or dev-bypass shape returned
      // empty). Leave the persisted value alone — the user may be
      // mid-session and a flash-empty-then-loaded would briefly clear
      // the active selection.
      return;
    }
    if (
      activePartnerId &&
      !availableOrgIds.includes(activePartnerId) &&
      !isPlatformAdmin
    ) {
      // Stale persisted value — a non-platform-admin user lost access to
      // that org since their last session. Reset to home. (Platform
      // admins keep their selection — see #1686 note above.)
      setActivePartner(homeOrgId ?? null);
      return;
    }
    if (!activePartnerId && homeOrgId) {
      // Fresh user / cleared localStorage — default to home.
      setActivePartner(homeOrgId);
    }
  }, [
    activePartnerId,
    availableOrgIds,
    homeOrgId,
    setActivePartner,
    isPlatformAdmin,
  ]);

  // Effect 2 — register the getter, refreshing the closure whenever
  // the activePartnerId changes.
  useEffect(() => {
    return setActiveOrgIdGetter(() => activePartnerId);
  }, [activePartnerId]);

  // Effect 3 (W128-F3) — invalidate the TanStack Query cache on org
  // switch. Without this, queries cached under org A continue to serve
  // through their query keys when the user has switched to org B, even
  // though new fetches correctly stamp `X-Org-Id: B`. Result: old org
  // A data renders briefly until the user-action-triggered refetch
  // resolves. Documented as the "cross-org stale-data race" in the
  // 2026-05-25 strategic audit (data-flow agent).
  //
  // Excludes auth.* + bootstrap queries (user-scoped, not org-scoped)
  // so the session doesn't have to re-bootstrap on every switch.
  const queryClient = useQueryClient();
  const previousOrgId = useRef<string | null>(null);
  useEffect(() => {
    const prev = previousOrgId.current;
    previousOrgId.current = activePartnerId;
    if (prev === null) {
      // Initial mount — don't invalidate (the cache is already cold).
      return;
    }
    if (prev === activePartnerId) {
      // Re-render with same value — no-op.
      return;
    }
    // Real switch. Invalidate everything except auth/bootstrap queries
    // (which are user-scoped, not org-scoped).
    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length === 0) return true;
        const root = key[0];
        if (root === 'auth' || root === 'memberships' || root === 'org-subtree')
          return false;
        return true;
      },
    });
  }, [activePartnerId, queryClient]);
}

/**
 * JSX-friendly wrapper for `useActiveOrgIdSync`. Renders `null`. Place
 * inside `<TenantProvider>` (so the hook has access to `useTenant`)
 * and after the QueryClient provider (so memberships can be fetched).
 */
export function ActiveOrgIdSync(): null {
  useActiveOrgIdSync();
  return null;
}
